import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Shield, DollarSign, Zap, Phone, Mail, User, MessageSquare, CheckCircle, CreditCard } from 'lucide-react';
import { CONTACT } from '../data/constants';

const CoversPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    productInterest: ''
  });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 5000);
    setFormData({ name: '', email: '', phone: '', message: '', productInterest: '' });
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };
  // Product categories: Filters, Covers, Cover Lifters, Rails, Steps
  const filterProducts = [
    {
      name: '2 Pack 6CH-940 Spa Filter Cartridge Replacement',
      description: 'Two-pack of 6CH-940 replacement filter cartridges — 6" diameter × 8¼" tall with a 1½" SAE threaded base. Fits many popular spa & hot tub brands to keep your water crystal clear. Buy online and pay securely with PayPal.',
      price: '$89.95',
      priceNote: 'plus shipping',
      buyNow: true,
      paypalAmount: '89.95',
      images: [
        'https://customer-assets-jt897jd0.emergentagent.net/job_product-pricing-27/artifacts/7pqp6xsf_unnamed.png',
        'https://customer-assets-jt897jd0.emergentagent.net/job_product-pricing-27/artifacts/ew1drxpk_unnamed-1.png'
      ]
    }
  ];

  const coverProducts = [
    {
      name: 'Custom Spa Cover',
      description: 'Made-to-fit, marine-grade insulated spa cover. To order, we\'ll need the exact dimensions of your spa, the spa manufacturer, and your color choice.',
      price: '$499.99 – $999.99',
      note: 'Custom built — dimensions, manufacturer & color required',
      images: [
        'https://images.unsplash.com/photo-1613490493576-7fde63acd811?w=851&q=80'
      ]
    }
  ];

  const lifterProducts = [
    {
      name: 'Pivot Top Cover Lifter',
      description: 'Pivot Top Mount spa & hot tub cover lift removal system with reinforced brackets. Easy off, easy on.',
      price: '$399.95 Installed',
      note: 'Installed to the tub',
      images: [
        'https://static.wixstatic.com/media/5c7c78_e3da0e551763403bba8eddb76d8df413~mv2.jpg/v1/fill/w_568,h_293,q_90,enc_avif,quality_auto/5c7c78_e3da0e551763403bba8eddb76d8df413~mv2.jpg',
        'https://static.wixstatic.com/media/5c7c78_d4fdcd6b4fbb4a3daeb5ad227de043d7~mv2.jpg/v1/fill/w_568,h_293,q_90,enc_avif,quality_auto/5c7c78_d4fdcd6b4fbb4a3daeb5ad227de043d7~mv2.jpg'
      ]
    },
    {
      name: 'Heavy-Duty Cover Lifter',
      description: 'No-drill, heavy-duty hot tub cover lifter that mounts under the tub. Adjustable height arm and extra-large footplate for stability.',
      price: '$349.99 Installed',
      note: 'Mounts under the tub',
      images: [
        'https://static.wixstatic.com/media/5c7c78_d6fbd76b73154fb19fd8064fb802083a~mv2.jpg/v1/fill/w_568,h_293,q_90,enc_avif,quality_auto/5c7c78_d6fbd76b73154fb19fd8064fb802083a~mv2.jpg',
        'https://static.wixstatic.com/media/5c7c78_f203494deeee4f0d808ee73d60126309~mv2.jpg/v1/fill/w_568,h_293,q_90,enc_avif,quality_auto/5c7c78_f203494deeee4f0d808ee73d60126309~mv2.jpg'
      ]
    },
    {
      name: 'Automatic ConvertALift "VacuSeal"',
      description: 'The ultimate conversion system that transforms the VacuSeal cover into an automatic cover lifter — unparalleled convenience, privacy, and safety.',
      price: '$4,999.00 Installed',
      images: [
        'https://static.wixstatic.com/media/5c7c78_481bd0d152d74cca812ac25eb74edb70~mv2.png/v1/fill/w_600,h_640,al_c,q_90,usm_0.66_1.00_0.01,enc_avif,quality_auto/SWIM%20SPAS%20SIMPSONVILLE%20SC%2C%20SWIM%20SPAS%20GREENVILLE%20SC%2C%20SWIM%20SPAS%2C%20SWIM%20SPAS%20IN%20GREENVILLE%20SC%2C.png'
      ]
    }
  ];

  const railProducts = [
    {
      name: 'Standard Hand Rail',
      description: 'Free-standing hand rail with a heavy weighted base that slides under the tub — no drilling and it does not attach to the spa. Sturdy support for safe, confident entry and exit.',
      price: '$349.99 Installed',
      note: 'Goes under the tub · does not attach',
      images: ['/images/accessories/rail-standard-underbase.png']
    },
    {
      name: 'Tub-Mounted Hand Rail',
      description: 'Hand rail that bolts securely to the side of your hot tub for rock-solid support right where you step in.',
      price: '$349.99 Installed',
      note: 'Attaches to the tub',
      images: ['/images/accessories/rail-tub-mounted.png']
    },
    {
      name: 'Adjustable 360° Swivel Hot Tub Rail',
      description: 'Adjustable-height (35"–58") rail with a 360° swivel handle for easy, secure access from any angle.',
      price: '$349.99 Installed',
      note: 'Adjustable height · 360° swivel',
      images: ['/images/accessories/rail-adjustable-360.png']
    }
  ];

  const stepProducts = [
    {
      name: '3-Step Spa Steps',
      description: 'Space-saving 3-tier hot tub steps with a built-in storage compartment under the top step. Compact 19.7"W × 23.6"D × 23.6"H — sturdy, stable and easy to assemble.',
      price: '$299.95',
      images: ['/images/accessories/steps-3-dimensions.png', '/images/accessories/steps-3-storage.png']
    },
    {
      name: '2-Step Spa Steps',
      description: 'Classic 2-tier spa steps for comfortable, secure entry.',
      price: '$99.95',
      images: ['https://customer-assets.emergentagent.com/job_river-spa-catalog/artifacts/jvkm6wp4_SWIM%20SPAS%20SIMPSONVILLE%20SC%2C%20SWIM%20SPAS%20GREENVILLE%20SC%2C%20SWIM%20SPAS%2C%20SWIM%20SPAS%20IN%20GREENVILLE%20SC%2C-1.avif']
    }
  ];

  const categories = [
    { key: 'filters', title: 'Spa Filters', items: filterProducts },
    { key: 'covers', title: 'Custom Covers', items: coverProducts },
    { key: 'lifters', title: 'Cover Lifters', items: lifterProducts },
    { key: 'rails', title: 'Rails & Railings', items: railProducts },
    { key: 'steps', title: 'Steps', items: stepProducts },
  ];

  return (
    <>
      <Helmet><title>Spa Filters, Hot Tub Covers & Lifters | Leisure Wellness Naples</title></Helmet>
      <div className="pt-36 pb-20" style={{ background: 'linear-gradient(180deg, #ffffff 0%, #E0FFFF 20%, #AFEEEE 50%, #98FB98 80%, #F0FFF0 100%)' }} data-testid="covers-page">
        <div className="max-w-6xl mx-auto px-4">
          {/* Hero */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-16">
            <h1 className="font-['Barlow_Condensed'] text-5xl md:text-6xl font-bold uppercase text-[#0E3D95] mb-4">
              Spa Filters, Covers, <span className="text-[#F88200]">Lifters &amp; Steps</span>
            </h1>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">Replacement filters ready to buy online — plus everything to protect your spa &amp; make entry easy!</p>
          </motion.div>

          {/* Hero Image */}
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }} className="mb-12">
            <img
              src="https://images.unsplash.com/photo-1613490493576-7fde63acd811?w=851&q=80"
              alt="Hot Tub Cover"
              className="w-full max-w-lg mx-auto rounded-lg shadow-xl"
            />
          </motion.div>

          {/* Pricing note */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12 bg-gradient-to-r from-[#0E3D95] to-[#1E5DBF] rounded-xl shadow-xl p-6 md:p-8 text-white text-center"
          >
            <h3 className="font-['Barlow_Condensed'] text-2xl md:text-3xl font-bold uppercase mb-2">
              Accessory Pricing
            </h3>
            <p className="text-blue-100 text-sm md:text-base max-w-3xl mx-auto">
              Custom covers, cover lifters, hand rails and steps are available as add-ons. Prices shown include professional installation where noted. Shipping is calculated by your delivery address.
            </p>
          </motion.div>

          {/* Products by category */}
          <div className="space-y-16">
            {categories.map((cat) => (
              <div key={cat.key} data-testid={`cover-category-${cat.key}`}>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-8 h-1 bg-[#F88200]"></div>
                  <h2 className="font-['Barlow_Condensed'] text-3xl md:text-4xl font-bold uppercase text-[#0E3D95]">{cat.title}</h2>
                </div>
                <div className="space-y-8">
                  {cat.items.map((product, idx) => (
                    <motion.div
                      key={`${cat.key}-${idx}`}
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      className="bg-white rounded-lg shadow-lg overflow-hidden"
                      data-testid={`cover-product-${cat.key}-${idx}`}
                    >
                      <div className="p-6 lg:p-8">
                        <div className="flex flex-col lg:flex-row gap-8">
                          <div className="flex-1">
                            <h3 className="font-['Barlow_Condensed'] text-2xl font-bold text-[#0E3D95] mb-3">{product.name}</h3>
                            <p className="text-slate-600 mb-4">{product.description}</p>
                            {product.buyNow ? (
                              <div data-testid={`buy-now-${cat.key}-${idx}`}>
                                <p className="text-3xl font-bold text-[#F88200]">
                                  {product.price}{' '}
                                  <span className="text-sm font-semibold text-slate-500">{product.priceNote}</span>
                                </p>
                                <form
                                  action="https://www.paypal.com/cgi-bin/webscr"
                                  method="post"
                                  target="_blank"
                                  rel="noopener"
                                  className="mt-4"
                                  data-testid={`paypal-form-${cat.key}-${idx}`}
                                >
                                  <input type="hidden" name="cmd" value="_xclick" />
                                  <input type="hidden" name="business" value="info@leisurewellnessnaples.com" />
                                  <input type="hidden" name="item_name" value={product.name} />
                                  <input type="hidden" name="amount" value={product.paypalAmount} />
                                  <input type="hidden" name="currency_code" value="USD" />
                                  <input type="hidden" name="no_shipping" value="2" />
                                  <button
                                    type="submit"
                                    className="inline-flex items-center gap-2 bg-[#0070BA] hover:bg-[#005ea6] text-white font-bold px-7 py-3 rounded-lg shadow-md transition-colors"
                                    data-testid={`paypal-buy-${cat.key}-${idx}`}
                                  >
                                    <CreditCard size={18} /> Buy Now with PayPal
                                  </button>
                                </form>
                                <p className="mt-2 text-xs text-slate-500">
                                  Secure checkout with PayPal. Shipping is added at checkout or invoiced to your address.
                                  Questions? Email <a href="mailto:info@leisurewellnessnaples.com" className="font-semibold text-[#0E3D95] hover:text-[#F88200]">info@leisurewellnessnaples.com</a>.
                                </p>
                              </div>
                            ) : (
                              <p className="text-xl font-bold text-[#F88200]">Call or email for pricing</p>
                            )}
                            {product.note && (
                              <p className="mt-2 text-sm font-semibold text-[#0E3D95] bg-orange-50 inline-block px-3 py-1 rounded-md border border-orange-200">
                                {product.note}
                              </p>
                            )}
                          </div>
                          <div className={`flex-1 grid gap-4 ${product.images.length > 1 ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1'}`}>
                            {product.images.map((img, i) => (
                              <img key={i} src={img} alt={product.name} className="w-full rounded-lg object-cover bg-slate-50" />
                            ))}
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Benefits */}
          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="mt-16 grid md:grid-cols-3 gap-8">
            {[
              { icon: Shield, title: 'Protection', desc: 'Keep your spa clean and protected from the elements year-round.' },
              { icon: DollarSign, title: 'Energy Savings', desc: 'Insulated covers reduce heating costs and energy consumption.' },
              { icon: Zap, title: 'Easy Access', desc: 'Cover lifters make it effortless to open and close your spa.' }
            ].map((b, i) => (
              <div key={i} className="text-center p-6">
                <b.icon className="mx-auto text-[#F88200] mb-3" size={36} />
                <h3 className="font-['Barlow_Condensed'] text-xl font-bold text-[#0E3D95] mb-2">{b.title}</h3>
                <p className="text-slate-600 text-sm">{b.desc}</p>
              </div>
            ))}
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-16 bg-white rounded-xl shadow-xl overflow-hidden"
          >
            <div className="bg-gradient-to-r from-[#0E3D95] to-[#1E5DBF] p-6 text-white">
              <h2 className="font-['Barlow_Condensed'] text-3xl font-bold uppercase mb-2">Inquire About Covers & Lifters</h2>
              <p className="text-blue-100">Contact us to purchase a cover lifter or spa cover</p>
            </div>
            <div className="p-6 md:p-8">
              {submitted ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="text-center py-8"
                >
                  <CheckCircle className="mx-auto text-green-500 mb-4" size={64} />
                  <h3 className="text-2xl font-bold text-[#0E3D95] mb-2">Thank You!</h3>
                  <p className="text-slate-600">We've received your inquiry and will contact you shortly.</p>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">
                        <User size={16} className="inline mr-2" />Name *
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#F88200] focus:border-transparent transition-all"
                        placeholder="Your name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">
                        <Mail size={16} className="inline mr-2" />Email *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#F88200] focus:border-transparent transition-all"
                        placeholder="your@email.com"
                      />
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">
                        <Phone size={16} className="inline mr-2" />Phone
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#F88200] focus:border-transparent transition-all"
                        placeholder="(239) 555-1234"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">Product Interest</label>
                      <select
                        name="productInterest"
                        value={formData.productInterest}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#F88200] focus:border-transparent transition-all"
                      >
                        <option value="">Select a product...</option>
                        <option value="custom-cover">Custom Spa Cover - $499.99 to $999.99</option>
                        <option value="pivot-lifter">Pivot Top Cover Lifter - $399.95 installed</option>
                        <option value="heavy-duty-lifter">Heavy-Duty Cover Lifter - $349.99 installed</option>
                        <option value="vacuseal">Automatic ConvertALift VacuSeal - $4,999 installed</option>
                        <option value="standard-rail">Standard Hand Rail - $349.99 installed</option>
                        <option value="swivel-rail">Adjustable 360° Swivel Rail - $349.99 installed</option>
                        <option value="steps-3">3-Step Spa Steps - $199.95</option>
                        <option value="steps-2">2-Step Spa Steps - $99.95</option>
                        <option value="other">Other / General Inquiry</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      <MessageSquare size={16} className="inline mr-2" />Message
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows={4}
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#F88200] focus:border-transparent transition-all resize-none"
                      placeholder="Tell us about your spa and what you're looking for..."
                    />
                  </div>
                  <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
                    <p className="text-sm text-slate-500">
                      Or call us directly: <a href={`tel:${CONTACT.phone.replace(/[^0-9]/g, '')}`} className="font-semibold text-[#0E3D95] hover:text-[#F88200]">{CONTACT.phone}</a>
                    </p>
                    <button
                      type="submit"
                      className="btn-primary px-8 py-3 text-lg"
                    >
                      Send Inquiry
                    </button>
                  </div>
                </form>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </>
  );
};

export default CoversPage;
